import { useMemo } from 'react';
import { mergeCueStateFromGetAndWs } from '@/helpers/perform-staging.helper';
import { entityRoomKeyFromEntityRef, usePerformControlWsStore } from '@/state/player/perform-control-ws.store';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types.ts';

/**
 * GET baseline ∪ Zustand overlay for auto-cue row phases (same room as `live-context-perform-control`).
 *
 * While a post-cue-command GET authority latch is active, prefer REST cueState so a stale
 * higher-pointer WS overlay cannot pin the list / Next after a backward jump.
 */
export function useMergedCueState(
  entityRef: string | undefined,
  cueStateFromGet: FlowCueStateV1 | undefined,
): FlowCueStateV1 | undefined {
  const roomKey = entityRoomKeyFromEntityRef(entityRef ?? '');
  const storeCueState = usePerformControlWsStore((s) => (roomKey ? s.cueStateByRoom[roomKey] : undefined));
  const hasGetAuthority = usePerformControlWsStore((s) =>
    roomKey ? s.cueGetAuthorityUntilSeqByRoom[roomKey] !== undefined : false,
  );

  return useMemo(() => {
    if (hasGetAuthority) {
      return cueStateFromGet ?? storeCueState;
    }
    return mergeCueStateFromGetAndWs(cueStateFromGet, storeCueState);
  }, [storeCueState, cueStateFromGet, hasGetAuthority]);
}
